import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Hash, Building2, CalendarDays, User, FileText, Gavel,
  Loader2, Bookmark, BookmarkCheck, FolderPlus, Scale,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface JurisprudenciaDetailDialogProps {
  jurisprudenciaId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When opened from chat, show "Adicionar na conversa" instead of "Perguntar à IA" */
  chatMode?: boolean;
  onAddToConversation?: (text: string) => void;
}

interface JurisprudenciaData {
  id: string;
  numero_processo: string;
  ementa: string;
  relator: string | null;
  data_julgamento: string | null;
  orgao_julgador: string | null;
  area_direito: string | null;
  palavras_chave: string[];
  texto_completo: string | null;
  tribunais: { sigla: string; nome: string } | null;
}

const extractRelatorFromTexto = (texto: string | null): string | null => {
  if (!texto) return null;
  const match = texto.match(/Relator(?:\(a\))?:\s*(.+?)(?:\n|$)/i);
  return match?.[1]?.trim() || null;
};

const getRelator = (item: JurisprudenciaData): string => {
  return item.relator || extractRelatorFromTexto(item.texto_completo) || "Não informado";
};

export const JurisprudenciaDetailDialog: React.FC<JurisprudenciaDetailDialogProps> = ({
  jurisprudenciaId,
  open,
  onOpenChange,
  chatMode = false,
  onAddToConversation,
}) => {
  const { user } = useAuth();
  const [item, setItem] = useState<JurisprudenciaData | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (!open || !jurisprudenciaId) return;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('jurisprudencias')
        .select('*, tribunais(sigla, nome)')
        .eq('id', jurisprudenciaId)
        .single();
      if (error || !data) {
        toast.error('Jurisprudência não encontrada');
        onOpenChange(false);
      } else {
        setItem(data as any);
      }
      setLoading(false);
    })();
  }, [open, jurisprudenciaId]);

  useEffect(() => {
    if (!user || !jurisprudenciaId || !open) return;
    supabase
      .from('jurisprudencia_saves')
      .select('id')
      .eq('user_id', user.id)
      .eq('jurisprudencia_id', jurisprudenciaId)
      .maybeSingle()
      .then(({ data }) => setIsSaved(!!data));
  }, [user, jurisprudenciaId, open]);

  const toggleSave = async () => {
    if (!user || !jurisprudenciaId) return;
    if (isSaved) {
      await supabase
        .from('jurisprudencia_saves')
        .delete()
        .eq('user_id', user.id)
        .eq('jurisprudencia_id', jurisprudenciaId);
      setIsSaved(false);
      toast.success('Removida dos salvos');
    } else {
      await supabase
        .from('jurisprudencia_saves')
        .insert({ user_id: user.id, jurisprudencia_id: jurisprudenciaId });
      setIsSaved(true);
      toast.success('Jurisprudência salva');
    }
  };

  const handleAddToConversation = () => {
    if (!item || !onAddToConversation) return;
    const text = [
      `**Jurisprudência: ${item.numero_processo}**`,
      item.tribunais ? `Tribunal: ${item.tribunais.sigla}` : '',
      `Relator: ${getRelator(item)}`,
      `Ementa: ${item.ementa.substring(0, 500)}`,
    ].filter(Boolean).join('\n');
    onAddToConversation(text);
    toast.success('Adicionado à conversa');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : item ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <Scale className="h-5 w-5 text-primary" />
                Detalhes da jurisprudência
              </DialogTitle>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t mt-3">
                <div className="flex items-start gap-2">
                  <Hash className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold">Processo</p>
                    <p className="text-sm text-muted-foreground font-mono">{item.numero_processo}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold">Órgão Julgador</p>
                    <p className="text-sm text-muted-foreground">{item.orgao_julgador || "Não informado"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CalendarDays className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold">Data de julgamento</p>
                    <p className="text-sm text-muted-foreground">
                      {item.data_julgamento
                        ? format(new Date(item.data_julgamento), "dd/MM/yyyy", { locale: ptBR })
                        : "Não informada"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold">Relator</p>
                    <p className="text-sm text-muted-foreground">{getRelator(item)}</p>
                  </div>
                </div>
                {item.tribunais && (
                  <div className="flex items-start gap-2">
                    <Gavel className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Tribunal</p>
                      <p className="text-sm text-muted-foreground">{item.tribunais.nome} ({item.tribunais.sigla})</p>
                    </div>
                  </div>
                )}
                {item.area_direito && (
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Área do Direito</p>
                      <p className="text-sm text-muted-foreground">{item.area_direito}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-3 flex-wrap">
                <Button variant="outline" size="sm" onClick={toggleSave}>
                  {isSaved ? (
                    <><BookmarkCheck className="h-4 w-4 mr-1 text-primary" /> Salva</>
                  ) : (
                    <><Bookmark className="h-4 w-4 mr-1" /> Salvar</>
                  )}
                </Button>
                {chatMode && onAddToConversation ? (
                  <Button variant="outline" size="sm" onClick={handleAddToConversation}>
                    <FolderPlus className="h-4 w-4 mr-1" /> Adicionar na conversa
                  </Button>
                ) : null}
                {item.palavras_chave?.slice(0, 5).map((kw, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                ))}
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto mt-4 space-y-4">
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Ementa</h4>
                <p className="text-sm leading-relaxed">{item.ementa}</p>
              </div>
              {item.texto_completo && (
                <div className="border-t pt-4">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Inteiro Teor</h4>
                  <div className="text-sm whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-md p-4">
                    {item.texto_completo}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
